import { redirect } from 'saddlebag-browser';

import BpkFloatingNotification from '@skyscanner/backpack-web/bpk-component-floating-notification';
import BpkIconHeart from '@skyscanner/backpack-web/bpk-component-icon/sm/heart';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { withI18n } from '../../contexts/i18n';
import { MeaningfulBehaviourDataWrapper } from '../../contexts/meaningful-behaviour-data';
import { withMiniEventTracker } from '../../contexts/mini-events';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { OnSavedStatusChanged } from '@shared/types/frontend/save-to-list';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './SaveHotelsFloatingNotification.module.scss';

import { SAVED_STATUS } from '@shared/constants/save-status';
import { buildHotelSaveAttributes } from '@shared/utils-frontend/meaningful-behaviour-data/hotel-save';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoSaveNotificationClicked } from '@shared/utils-frontend/mini-events/hotels-action';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  saveStatus: string;
  statusText?: string;
  onSavedStatusChanged: OnSavedStatusChanged;
  clickArea: string;
  hotelId: string;
  miniEventTracker: MiniEventTracker;
};

const SaveHotelsFloatingNotification = ({
  clickArea,
  hotelId,
  i18n: { translate },
  miniEventTracker: { trackHotelsAction },
  onSavedStatusChanged,
  saveStatus,
  statusText,
}: Props) => {
  const getStatusText = () => {
    if (statusText) {
      return statusText;
    }

    return translate('SaveHotelsFloatingNotification_NetworkError_Title');
  };

  const isSavedStatus = saveStatus === SAVED_STATUS.SAVED;
  const viewSavedHotels = translate('ktxt_view_saved_hotels_CTA_a11y_label');
  return (
    <div
      role="button"
      aria-hidden={!isSavedStatus}
      tabIndex={isSavedStatus ? 0 : -1}
      aria-label={isSavedStatus ? viewSavedHotels : ''}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onKeyDown={() => {}}
      className={cls('SaveHotelsFloatingNotification__container')}
    >
      {isSavedStatus && (
        <MeaningfulBehaviourDataWrapper
          {...buildHotelSaveAttributes({ hotelId })}
        >
          <BpkFloatingNotification
            className={cls('SaveHotelsFloatingNotification')}
            icon={BpkIconHeart}
            text={getStatusText()}
            ctaText={translate('SaveHotelsFloatingNotification_Saved_Button')}
            data-testid="save-hotel-success"
            // @ts-expect-error: TS2322: Type '(e: Event) => void' is not assignable to type '(e: MouseEvent<HTMLButtonElement, MouseEvent>) => void'.
            onClick={(e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              trackHotelsAction(
                ACTION_TYPE.SAVE_NOTIFICATION_CLICKED,
                buildAdditionalInfoSaveNotificationClicked({
                  clickArea,
                  hotelId,
                }),
              );
              redirect('/profile/saved');
            }}
            onExit={() => {
              onSavedStatusChanged({
                payload: {},
                saveStatus: SAVED_STATUS.RESET,
              });
            }}
          />
        </MeaningfulBehaviourDataWrapper>
      )}
      {saveStatus === SAVED_STATUS.REMOVED && (
        <BpkFloatingNotification
          className={cls('SaveHotelsFloatingNotification')}
          text={getStatusText()}
          onExit={() => {
            onSavedStatusChanged({
              payload: {},
              saveStatus: SAVED_STATUS.RESET,
            });
          }}
        />
      )}
      {saveStatus === SAVED_STATUS.ERROR && (
        <BpkFloatingNotification
          className={cls('SaveHotelsFloatingNotification')}
          text={getStatusText()}
          onExit={() => {
            onSavedStatusChanged({
              payload: {},
              saveStatus: SAVED_STATUS.RESET,
            });
          }}
        />
      )}
    </div>
  );
};

export default withMiniEventTracker(withI18n(SaveHotelsFloatingNotification));
